import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { chartColorPallet as DEFAULT_COLORS } from '@/config/colorPallets/universalDesignSystem';
import { getClickPositionFromEvent } from '@/utils/dom';
import { deleteUndefinedValues } from '@/utils/objects';
import { getContextEditingSectionAttr, selectEditingArea } from '@/components/Editor/hooks/useEditingAreas';
import { checkIsGrouped, getBorderRadiusFromConfig, getBorderSkipped } from './utils';
import { buildChartRenderingHash, extractDataLabelProperty, getChartTitleAnnotaionSettings, getSectionCanvasId } from '../utils';
import { useBarStyles } from './stylesTheme';
import settings, { selectionConfig, labelsSelectionConfig, legendsSelectionConfig } from './settings';
import StyleVariables, { getSectionAttr, getSectionAttrSelector } from '../../../misc/StyleVariables';
import useSelections from '../../../misc/useSelections';
import Header from '../../misc/Header';
import Loading from './Loading';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DATA_LABEL_MIN_BAR_HEIGHT = 20;

const BarChart = _ref => {
  let {
    widgetId,
    sectionId,
    elementId,
    stacked = false,
    editing = false,
    isLoading = false,
    hasNoData = false,
    hasLegend: defaultHasLegend = false,
    config,
    chartData,
    generateLegendLabels,
    toggleLegendVisibility,
    xAxisTicksCallback,
    yAxisTicksCallback,
    openLegendsContextEditor
  } = _ref;
  const {
    barThemeStyles
  } = useBarStyles(config);
  const {
    // selections = '',
    chartType = 'vertical',
    animation = {}
  } = config;
  const {
    0: isLoadingDeps,
    1: setIsLoadingDeps
  } = useState(false);
  const {
    0: hasLoadedDeps,
    1: setHasLoadedDeps
  } = useState(false); // Load Chart Deps

  useEffect(() => {
    if (isLoadingDeps || hasLoadedDeps) {
      return;
    }

    setIsLoadingDeps(true);

    (async () => {
      const {
        default: Chart,
        LinearScale,
        Tooltip,
        Legend,
        CategoryScale
      } = await import('chart.js/auto');
      const {
        default: ChartDataLabels
      } = await import('chartjs-plugin-datalabels');
      const {
        default: ChartAnnotation
      } = await import('chartjs-plugin-annotation');
      const {
        default: CustomBackground
      } = await import('../../plugins/customBackground');
      const {
        default: ActiveDatasetHighlight
      } = await import('../../plugins/activeDatasetHighlightPlugin');
      const {
        default: ResponsiveResizePlugin
      } = await import('../../plugins/responsiveResizePlugin');
      const {
        default: LegendPaddingPlugin
      } = await import('../../plugins/legendPadding');
      const {
        default: AxisLabelClickPlugin
      } = await import('../../plugins/axisLabelClickPlugin');
      Chart.register(CategoryScale, LinearScale, Tooltip, Legend, ChartDataLabels, ChartAnnotation, CustomBackground, ActiveDatasetHighlight, ResponsiveResizePlugin, LegendPaddingPlugin, AxisLabelClickPlugin); // Disable datalabels by default

      if (Chart.defaults.plugins.datalabels) {
        Chart.defaults.plugins.datalabels.display = false;
      }

      setHasLoadedDeps(true);
      setIsLoadingDeps(false);
    })(); // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []); // Data & Loading States

  const hasDrawableData = Boolean(chartData.datasets.length);
  const isShowingLoader = !hasLoadedDeps || !hasDrawableData;
  const chartRenderingHash = buildChartRenderingHash({
    animation,
    themeStyles: barThemeStyles
  }) + `${config.yAxisPosition}-${chartType}`; // Create chart options

  const options = useMemo(() => {
    var _config$styles;

    const animation = config.animation || {};
    const isHorizontal = chartType === 'horizontal';
    const indexAxis = isHorizontal ? 'y' : 'x';
    const chartTitle = config.chartTitle ? {
      display: true,
      text: config.chartTitle,
      font: {
        family: barThemeStyles.titleFontFamily,
        size: barThemeStyles.titleFontSize,
        lineHeight: barThemeStyles.titleLineHeight
      },
      color: barThemeStyles.titleColor
    } : {};
    const legendPosition = config.legendPosition || 'bottom';
    const hasLegend = config.hasLegend ?? defaultHasLegend;
    const isYAxisTitlePositionOver = config.yAxisPosition === 'over';
    const isAnimationDisabled = animation.enabled !== undefined && !animation.enabled;
    const axisFont = {
      font: {
        family: barThemeStyles.chartAxisFontFamily,
        size: barThemeStyles.chartAxisFontSize,
        weight: barThemeStyles.chartAxisFontWeight
      },
      color: barThemeStyles.chartAxisFontColor
    };
    return {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis,
      // Bar and category percentage stored as 0-100
      // Convert to 0-1 for chart.js
      barPercentage: barThemeStyles.barPercentage !== undefined ? barThemeStyles.barPercentage / 100 : undefined,
      categoryPercentage: barThemeStyles.categoryPercentage !== undefined ? barThemeStyles.categoryPercentage / 100 : undefined,
      animation: _objectSpread(_objectSpread({}, deleteUndefinedValues(animation)), {}, {
        duration: isAnimationDisabled ? 0 : animation.duration
      }),
      elements: {
        bar: {
          borderWidth: 0,
          borderSkipped: getBorderSkipped,
          borderRadius: getBorderRadiusFromConfig(barThemeStyles)
        }
      },
      plugins: {
        title: chartTitle,
        annotation: isYAxisTitlePositionOver && config.yAxisTitle ? getChartTitleAnnotaionSettings({
          title: config.yAxisTitle,
          axis: isHorizontal ? 'x' : 'y',
          widgetId,
          sectionId,
          axisFont
        }) : undefined,
        datalabels: {
          clip: true,
          display: 'auto',
          color: context => extractDataLabelProperty(context, 'color', barThemeStyles.chartDataLabelColor),
          align: context => extractDataLabelProperty(context, 'align', 'center'),
          anchor: context => extractDataLabelProperty(context, 'anchor', 'end'),
          rotation: context => extractDataLabelProperty(context, 'rotation', 0),
          clamp: context => extractDataLabelProperty(context, 'clamp', true),
          formatter: function (value, context) {
            var _context$dataset$data;

            const chart = context.chart;
            const meta = chart.getDatasetMeta(context.datasetIndex);
            const model = meta.data[context.dataIndex];
            const {
              height
            } = model.getProps(['height'], true); // Hide labels if bar is too small

            if (height < DATA_LABEL_MIN_BAR_HEIGHT) return ''; // Hide labels if not enabled or with value 0

            const dataLabel = (_context$dataset$data = context.dataset.datalabels) === null || _context$dataset$data === void 0 ? void 0 : _context$dataset$data[context.dataIndex];
            if (!(dataLabel !== null && dataLabel !== void 0 && dataLabel.display) || value === 0) return '';
            return dataLabel.template ?? value;
          }
        },
        legend: deleteUndefinedValues({
          align: ['top', 'bottom'].includes(legendPosition) ? 'start' : undefined,
          position: legendPosition,
          display: hasLegend,
          labels: {
            pointStyle: 'rectRounded',
            usePointStyle: true,
            font: {
              family: barThemeStyles.legendFontFamily,
              size: barThemeStyles.legendFontSize,
              lineHeight: barThemeStyles.legendLineHeight
            },
            color: barThemeStyles.legendColor,
            // Remove unwanted datasets from legend
            filter: item => item.text !== undefined,
            // Custom legend labels
            generateLabels: chartData.legends && generateLegendLabels ? chart => generateLegendLabels(chart, (chartData === null || chartData === void 0 ? void 0 : chartData.legends) || [], barThemeStyles.legendColor) : undefined
          },
          onClick: editing && openLegendsContextEditor ? e => openLegendsContextEditor(e, widgetId, sectionId) : toggleLegendVisibility ?? undefined
        }),
        tooltip: {
          enabled: true,
          usePointStyle: true,
          cornerRadius: 0,
          callbacks: {
            title: _ref2 => {
              let [item] = _ref2;
              // @ts-ignore
              return (item === null || item === void 0 ? void 0 : item.dataset.titles[item.dataIndex]) || item.label;
            },
            label: item => {
              return ` ${// @ts-ignore
              (item === null || item === void 0 ? void 0 : item.dataset.templates[item.dataIndex]) || item.parsed.y}`;
            }
          }
        },
        legendPadding: {
          padding: 25 // Custom padding between legend and chart

        },
        activeDatasetHighlightPlugin: {
          enable: editing,
          dimOpacity: 0.15,
          // Opacity for non-hovered lines.
          hoverCursor: 'pointer',
          extraElements: [`[${getContextEditingSectionAttr()}]`, '[data-floating-ui-portal]']
        },
        customCanvasBackgroundColor: {
          color: (config === null || config === void 0 ? void 0 : (_config$styles = config.styles) === null || _config$styles === void 0 ? void 0 : _config$styles.backgroundColor) || barThemeStyles.chartBackgroundColor
        },
        axisLabelClickPlugin: {
          enabled: true,
          onAxisClick: (event, position) => handleAxisClick({
            widgetId,
            sectionId
          }, event, position)
        }
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: isYAxisTitlePositionOver && config.yAxisTitle ? 40 : 0,
          bottom: hasLegend || config.xAxisTitle ? 0 : -barThemeStyles.xAxisTicksPadding
        }
      },
      scales: {
        x: deleteUndefinedValues({
          min: isHorizontal ? config.xScaleMin : undefined,
          max: isHorizontal ? config.xScaleMax : undefined,
          grid: {
            color: barThemeStyles.xAxisGridVisible ?? barThemeStyles.xAxisGridVisible ? barThemeStyles.xAxisGridColor : 'transparent',
            drawTicks: false,
            offset: true,
            tickLength: 20,
            lineWidth: barThemeStyles.xAxisGridBorderWidth ?? 1
          },
          border: {
            dash: barThemeStyles.xAxisGridBorderDash ?? [5, 5],
            display: false
          },
          ticks: deleteUndefinedValues(_objectSpread(_objectSpread({}, axisFont), {}, {
            display: config.xAxisDisplay ?? true,
            padding: barThemeStyles.xAxisTicksPadding,
            crossAlign: 'near',
            // Custom value ticks callback for horizontal bar charts
            callback: xAxisTicksCallback ? xAxisTicksCallback : undefined
          })),
          stacked: stacked,
          title: config.xAxisTitle ? _objectSpread({
            display: config.xAxisDisplay ?? true,
            text: config.xAxisTitle,
            align: config.xAxisPosition
          }, axisFont) : undefined
        }),
        y: deleteUndefinedValues({
          min: !isHorizontal ? config.yScaleMin : undefined,
          max: !isHorizontal ? config.yScaleMax : undefined,
          grid: {
            color: barThemeStyles.yAxisGridVisible ?? barThemeStyles.yAxisGridVisible ? barThemeStyles.yAxisGridColor : 'transparent',
            drawTicks: false,
            lineWidth: barThemeStyles.yAxisGridBorderWidth ?? 1
          },
          border: {
            dash: barThemeStyles.yAxisGridBorderDash ?? [5, 5],
            display: false
          },
          ticks: deleteUndefinedValues(_objectSpread(_objectSpread({}, axisFont), {}, {
            display: config.yAxisDisplay ?? true,
            padding: barThemeStyles.yAxisTicksPadding,
            // Custom value ticks callback for vertical bar charts
            callback: yAxisTicksCallback ? yAxisTicksCallback : undefined
          })),
          stacked: stacked,
          title: config.yAxisTitle && !isYAxisTitlePositionOver ? _objectSpread({
            display: config.yAxisDisplay ?? true,
            text: config.yAxisTitle,
            align: config.yAxisPosition
          }, axisFont) : undefined
        })
      },
      onClick: editing ? (event, activeElements) => {
        setSliceAsEditingArea({
          widgetId,
          sectionId,
          chartData
        }, event, activeElements);
      } : undefined
    };
  }, [config, chartType, stacked, chartData, widgetId, sectionId, editing, defaultHasLegend, xAxisTicksCallback, yAxisTicksCallback, generateLegendLabels, toggleLegendVisibility, openLegendsContextEditor, barThemeStyles]);
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread(_objectSpread({}, elementId && {
    id: elementId
  }), {}, {
    className: styles.container
  }, getSectionAttr(widgetId, sectionId)), {}, {
    children: [/*#__PURE__*/_jsx(Header, {
      editing: editing,
      widgetId: widgetId,
      sectionId: sectionId,
      config: config,
      className: styles.header
    }), isLoading || hasNoData ? /*#__PURE__*/_jsx(Loading, {
      chartType: chartType,
      editing: editing
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [isShowingLoader ? /*#__PURE__*/_jsx(Loading, {
        chartType: chartType
      }) : /*#__PURE__*/_jsx(_Fragment, {}), /*#__PURE__*/_jsx("div", {
        className: clsx(styles.main, isShowingLoader ? styles['main--hide'] : ''),
        children: hasLoadedDeps ? /*#__PURE__*/_jsx(Bar, {
          id: getSectionCanvasId(widgetId, sectionId) // @ts-expect-error - datasets are extended with custom properties
          ,
          data: chartData,
          options: options
        }, chartRenderingHash) : /*#__PURE__*/_jsx(_Fragment, {})
      })]
    }), /*#__PURE__*/_jsx(StyleVariables, {
      selector: getSectionAttrSelector(widgetId, sectionId),
      data: _objectSpread(_objectSpread({}, (settings === null || settings === void 0 ? void 0 : settings.styles) || {}), (config === null || config === void 0 ? void 0 : config.styles) || {})
    })]
  }));
};

export default BarChart;
export { DEFAULT_COLORS, useSelections, selectionConfig, labelsSelectionConfig, legendsSelectionConfig, checkIsGrouped };

// Apply user selections of an individual
// chart slice as the active editing area
function setSliceAsEditingArea(_ref3, event, activeElements) {
  let {
    widgetId,
    sectionId,
    chartData = {}
  } = _ref3;
  if (event.type !== 'click') return;
  const editingContext = {
    widgetId,
    sectionId,
    chartData
  }; // If empty or only one dataset, treat as empty area click

  if (chartData.datasets.length <= 1 || activeElements.length === 0) {
    handleEmptyAreaClick(editingContext, event);
    return;
  }

  const [item] = activeElements;
  const dataset = chartData.datasets[item.datasetIndex] || {
    data: []
  };
  const selection = dataset.selections[item.index] || '';
  const selectionIndex = dataset.selectionIndexes[item.index] ?? -1;
  if (selectionIndex < 0) return; // Send selection

  selectEditingArea({
    widgetId,
    sectionId,
    sectionEditingScope: 'selections',
    sectionEditingArea: `item-${selectionIndex}`,
    clickPosition: getClickPositionFromEvent(event.native),
    editingAreaMeta: JSON.stringify({
      selection,
      selectionIndex,
      collectionIndex: dataset.collectionIndex ?? item.datasetIndex
    })
  });
} // Handles clicks in empty chart areas.


const handleEmptyAreaClick = (context, event) => {
  selectEditingArea({
    widgetId: context.widgetId,
    sectionId: context.sectionId,
    sectionEditingScope: 'chart',
    sectionEditingArea: 'chart',
    editingAreaMeta: '',
    clickPosition: getClickPositionFromEvent(event.native)
  });
};

const handleAxisClick = (context, event, position) => {
  selectEditingArea({
    widgetId: context.widgetId,
    sectionId: context.sectionId,
    sectionEditingScope: 'chart-axes',
    sectionEditingArea: 'chart-axes',
    editingAreaMeta: JSON.stringify({
      axis: position.axis
    }),
    clickPosition: getClickPositionFromEvent(event.native)
  });
};